import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, IconButton, InputAdornment, SxProps, Theme, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { TextField } from '~/components/TextField';
import { ADMIN_ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';
import { useLoginMutation } from '~/services/rtkQuery/user';
import { LoginFormValues } from '~/types/formValues';
import { loginFormSchema } from '~/utils/formSchemas';

import styles from './styles.module.scss';

const TEXT_FIELD_SX: SxProps<Theme> = { mb: '32px' };

export const LoginPage: React.FC = () => {
  const location = useLocation();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const passwordRef = useRef<HTMLDivElement>(null);
  const [login] = useLoginMutation();
  const { handleApiError } = useNotification();

  const isAdminRoute = useMemo(() => {
    return location.pathname.startsWith(ADMIN_ROUTES.HOME);
  }, [location.pathname]);

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginFormSchema,
    validateOnMount: true,
    onSubmit: async values => {
      try {
        await login({ ...values, admin_page: isAdminRoute }).unwrap();
      } catch (e) {
        handleApiError(e);
      }
    },
  });

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (formik.isValid && !formik.isSubmitting) {
          formik.handleSubmit();
        }
      }
    },
    [formik],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  useUpdateEffect(() => {
    passwordRef.current?.focus();
  }, [passwordVisible]);

  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible(prevValue => !prevValue);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.container__content}>
        <form onSubmit={formik.handleSubmit}>
          {isAdminRoute && (
            <Typography align="center" color="text.primary" mb="30px" variant="h4">
              ADMIN PANEL
            </Typography>
          )}
          <Typography color="text.primary" mb="40px" variant="h5">
            Login
          </Typography>
          <TextField
            error={formik.touched.email && !!formik.errors.email}
            helperText={formik.touched.email ? formik.errors.email : undefined}
            label="Email"
            name="email"
            sx={TEXT_FIELD_SX}
            type="email"
            value={formik.values.email}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <TextField
            error={formik.touched.password && !!formik.errors.password}
            helperText={formik.touched.password ? formik.errors.password : undefined}
            inputRef={passwordRef}
            label="Password"
            name="password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility}>
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={TEXT_FIELD_SX}
            type={passwordVisible ? 'text' : 'password'}
            value={formik.values.password}
            variant="outlined"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
          <Button disabled={!formik.isValid} loading={formik.isSubmitting} type="submit">
            Login
          </Button>
        </form>
      </div>
    </div>
  );
};
